import { v4 as uuidv4 } from "uuid";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { FormattedRpServiceConfigurationServiceOffering, RpServiceConfigurationServiceOffering } from "@idw-types/data/rp-service-configuration";
import { AxnServiceOfferingDataSource } from "@idw-types/data/reference-data/axn";
import { ApAssertion, Assertion, FallbackServiceMapping, ServiceOffering } from "@idw-types/data/reference-data/generic";
import { formatRpServiceConfigurationServiceOfferingAssertions } from "./rp-service-configuration-service-offering-assertion-formatter";
import { getDefaultServiceOfferingWeight } from "@utils/scores/trust-score-util";

export const formatRpServiceConfigurationServiceOfferings = (
    rpServiceConfigurationServiceOfferings: Array<RpServiceConfigurationServiceOffering>,
    serviceOfferings: Array<ServiceOffering>,
    apAssertions: Array<ApAssertion>,
    assertions: Array<Assertion>,
    serviceOfferingDataSources: Array<AxnServiceOfferingDataSource>,
    fallbackServiceMappings: Array<FallbackServiceMapping>
): Array<FormattedRpServiceConfigurationServiceOffering> => {

    const finalServiceOfferings: Array<FormattedRpServiceConfigurationServiceOffering> = [];

    serviceOfferings.forEach(serviceOffering => {
        const serviceOfferingsWithServiceOfferingId = rpServiceConfigurationServiceOfferings.filter(so => so.serviceOfferingID === serviceOffering.id);

        if (serviceOfferingsWithServiceOfferingId.length > 0) {
            serviceOfferingsWithServiceOfferingId.forEach(soWithSOID => {
                const formattedSoWithSOID = formatRpServiceConfigurationServiceOffering(
                    soWithSOID,
                    serviceOffering,
                    apAssertions,
                    assertions,
                    serviceOfferingDataSources,
                    fallbackServiceMappings
                );
    
                finalServiceOfferings.push(formattedSoWithSOID);
            });
        } else {
            const formattedSoWithSOID = formatRpServiceConfigurationServiceOffering(
                null,
                serviceOffering,
                apAssertions,
                assertions,
                serviceOfferingDataSources,
                fallbackServiceMappings
            );

            finalServiceOfferings.push(formattedSoWithSOID);
        }
    });

    return finalServiceOfferings;
};

export const formatRpServiceConfigurationServiceOffering = (
    rpServiceConfigurationServiceOffering: RpServiceConfigurationServiceOffering,
    serviceOffering: ServiceOffering,
    apAssertions: Array<ApAssertion>,
    assertions: Array<Assertion>,
    serviceOfferingDataSources: Array<AxnServiceOfferingDataSource>,
    fallbackServiceMappings: Array<FallbackServiceMapping>
): FormattedRpServiceConfigurationServiceOffering => {
    const serviceOfferingApAssertions = apAssertions.filter(apAssertion => apAssertion.serviceOfferingID === serviceOffering.id);
    const serviceOfferingApAssertionIds = serviceOfferingApAssertions.map(apAssertion => apAssertion.assertionID);
    const serviceOfferingAssertions = assertions.filter(assertion => serviceOfferingApAssertionIds.includes(assertion.id));

    const formattedServiceOfferingAssertions = formatRpServiceConfigurationServiceOfferingAssertions(
        rpServiceConfigurationServiceOffering,
        serviceOffering,
        serviceOfferingApAssertions,
        serviceOfferingAssertions
    );

    const dataSources = serviceOfferingDataSources.filter(ds => ds.serviceOfferingID === serviceOffering.id);
    const fallbackServiceOfferingIds = fallbackServiceMappings
        .filter(fsm => fsm.serviceOfferingID === serviceOffering.id)
        .map(fsm => fsm.fallbackServiceOfferingID);

    return {
        affectsIDWRiskScore: serviceOffering.affectsIDWRiskScore,
        assertions: formattedServiceOfferingAssertions,
        dataSource: rpServiceConfigurationServiceOffering?.dataSource || "prod",
        fallbackRPServiceSOID: rpServiceConfigurationServiceOffering?.fallbackRPServiceSOID || null,
        fallbackServiceOfferingID: rpServiceConfigurationServiceOffering?.fallbackServiceOfferingID || null,
        fallbackStatus: rpServiceConfigurationServiceOffering?.fallbackStatus || null,
        id: rpServiceConfigurationServiceOffering?.id || null,
        order: rpServiceConfigurationServiceOffering?.order ? parseInt(rpServiceConfigurationServiceOffering.order) : null,
        processingType: serviceOffering.processingType,
        processingTypeID: serviceOffering.processingTypeID,
        referenceCode: serviceOffering.referenceCode,
        refreshRate: rpServiceConfigurationServiceOffering?.refreshRate || "EachLogin",
        rpServiceID: rpServiceConfigurationServiceOffering?.rpServiceID || null,
        serviceOfferingID: serviceOffering.id,
        status: rpServiceConfigurationServiceOffering?.status || EntityStatusTypes.INACTIVE,
        serviceOfferingStatus: serviceOffering.status || EntityStatusTypes.INACTIVE,
        weight: rpServiceConfigurationServiceOffering?.weight || getDefaultServiceOfferingWeight(serviceOffering.affectsIDWRiskScore),
        category: serviceOffering.category,
        name: serviceOffering.name,
        shortName: serviceOffering.shortName,
        description: serviceOffering.description,
        dataSources: dataSources,
        fallbackServiceOfferingIds: fallbackServiceOfferingIds,
        addedToService: !!rpServiceConfigurationServiceOffering,
        uuid: uuidv4()
    };
};